import React, { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { Product } from '../../types/product';
import { fetchProductDemos, getDcloudDemoId, groupDemos } from '../../support/dcloudService';
import DcloudSessionManager from './DCloud/DcloudSessionManager';
import { DcloudScheduleTarget } from './DCloud/DcloudScheduleSessionModal';
import {
  HbrIcon,
  HbrSpinner,
  HbrDrawer,
  HbrDropdown,
  HbrMenu,
  HbrMenuItem,
  HbrButton,
  HbrOption,
  HbrSelect,
} from '@harbor/elements-react-standalone';
import { x } from '@harbor/elements/icons';
import { useFetchUser } from '../../hooks/user-hooks';
import { useSelector } from 'react-redux';
import { RootState } from '../../support/store';
import {
  useAddCustomerProductAssignmentsMutation,
  useDeleteCustomerProductAssignmentsMutation,
  useFetchCustomerProductAssignments,
  useFetchCustomerProducts,
} from '../../hooks/customer-hooks';
import {
  useAddOpportunityProductAssignmentsMutation,
  useDeleteOpportunityProductAssignmentsMutation,
  useFetchOpportunityProductAssignments,
  useFetchOpportunityProducts,
} from '../../hooks/opportunity-hooks';
import { CacheKey } from '../../types/cache-keys';
import StbMultiSelectGroupedWithSearch from '../security-toolbox-ui/StbMultiSelectGroupedWithSearch';
import StbButton from '../security-toolbox-ui/StbButton';
import StbModal from '../security-toolbox-ui/StbModal';
import { asSingleValue } from '../../support/utils';
import {
  APPLIANCE_OFFER_TYPES,
  EMPTY_DISPLAY,
  HYBRID_OFFER_TYPES,
  ProductDeploymentSelection,
  ProductRelationshipSelection,
  SAAS_DEPLOYMENT_ID,
  SAAS_OFFER_TYPES,
  buildCustomerOwnedProductPayload,
  buildDeploymentOptions,
  buildOpportunityProductPayload,
  displayValue,
  expandRelationshipSelectionsToRows,
  getApplianceNameFromRelationship,
  getBaseProductId,
  getDeploymentNameFromRelationship,
  getDeploymentRowsFromRelationshipRows,
  getInitialDeploymentRowsForProduct,
  getLicenseNameFromRelationship,
  getLicenseOptionsForDeployment,
  getProductOfferType,
  getVersionNameFromRelationship,
  getVersionOptionsForDeployment,
  hydrateProductList,
  isSaasDeployment,
  makeRelationshipRowId,
  normalizeDeploymentId,
  normalizeOfferType,
  shouldOmitProductFromRelationshipUi,
  shouldShowLicenseForProduct,
  shouldShowVersionForDeployment,
  toNumberOrNull,
} from '../../support/productRelationshipUtils';

interface ProductTagManagerProps {
  title: string;
  isCisco: boolean;
  editable: boolean;
  productKind: 'customer' | 'opportunity';
  opportunityID?: string;
  customerId?: string;
  drawerView?: boolean;
}

const styles = {
  tag: [
    'rounded',
    'font-semibold',
    'px-5',
    'py-1',
    'text-sm',
    'hover:text-black',
    'flex space-between',
    'leading-5',
    'items-start',
    'group',
    'pr-7',
    'transition-colors',
    'duration-300',
  ].join(' '),
  tagIcon: [
    'self-start',
    'ml-1',
    'mt-[-1px]',
    'p-1',
    'hidden',
    'hover:text-black',
    'group-hover:cursor-pointer',
    'pb-0',
    'text-xs',
    'group-hover:block',
    'transition-colors',
    'duration-300',
  ].join(' '),
};

const emptyRelationship: ProductRelationshipSelection = {
  deployments: [],
};

const deploymentSelectionKey = (selection: ProductRelationshipSelection) => {
  const rows = [...(selection.deployments ?? [])].map((row) => ({
    deployment_id: row.deployment_id ?? null,
    version_id: row.version_id ?? null,
    license_id: row.license_id ?? null,
  }));

  rows.sort((a, b) => {
    const aKey = `${a.deployment_id ?? ''}-${a.version_id ?? ''}-${a.license_id ?? ''}`;
    const bKey = `${b.deployment_id ?? ''}-${b.version_id ?? ''}-${b.license_id ?? ''}`;
    return aKey.localeCompare(bKey);
  });

  return JSON.stringify(rows);
};

const getRelationshipSelectionFromRows = (
  rows: any[],
  product: any,
  productCatalog: any[] = []
): ProductRelationshipSelection => {
  return {
    deployments: getDeploymentRowsFromRelationshipRows(rows, product, productCatalog),
  };
};

const ProductTagManager: React.FC<ProductTagManagerProps> = ({
                                                               title,
                                                               isCisco,
                                                               editable,
                                                               productKind,
                                                               opportunityID,
                                                               customerId,
                                                             }) => {
  const queryClient = useQueryClient();

  const { data: user } = useFetchUser();
  const allProducts = useSelector((state: RootState) => state.products.items);

  const { data: customerProducts } = useFetchCustomerProducts(customerId, {}, { enabled: !!customerId });
  const { data: customerProductAssignments } = useFetchCustomerProductAssignments({ customer: customerId }, { enabled: !!customerId });
  const addCustomerProducts = useAddCustomerProductAssignmentsMutation();
  const deleteCustomerProducts = useDeleteCustomerProductAssignmentsMutation();

  const { data: opportunityProducts } = useFetchOpportunityProducts(opportunityID, {}, { enabled: !!opportunityID });
  const { data: opportunityProductAssignments } = useFetchOpportunityProductAssignments({ opportunity: opportunityID }, { enabled: !!opportunityID });
  const addOpportunityProducts = useAddOpportunityProductAssignmentsMutation();
  const deleteOpportunityProducts = useDeleteOpportunityProductAssignmentsMutation();

  const [allSelectableProducts, setAllSelectableProducts] = useState<Product[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);
  const [updatedSelectedProducts, setUpdatedSelectedProducts] = useState<any[]>([]);
  const [relationshipSelections, setRelationshipSelections] = useState<Record<number, ProductRelationshipSelection>>({});

  const [removeModalVisible, setRemoveModalVisible] = useState<boolean>(false);
  const [editModalVisible, setEditModalVisible] = useState<boolean>(false);

  const [drawerVisible, setDrawerVisible] = useState<boolean>(false);
  const [selectedProductDetails, setSelectedProductDetails] = useState<any | null>(null);
  const [loadingDcloudData, setLoadingDcloudData] = useState<boolean>(false);
  const [dcloudData, setDcloudData] = useState<any>(null);
  const [dcloudError, setDcloudError] = useState<string | null>(null);
  const [dcloudSessionsVisible, setDcloudSessionsVisible] = useState<boolean>(false);
  const [dcloudScheduleTarget, setDcloudScheduleTarget] = useState<DcloudScheduleTarget | null>(null);

  const [stagedRemovedProducts, setStagedRemovedProducts] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const activeAssignments = useMemo(() => {
    if (productKind === 'customer') return customerProductAssignments ?? [];
    if (productKind === 'opportunity') return opportunityProductAssignments ?? [];
    return [];
  }, [productKind, customerProductAssignments, opportunityProductAssignments]);

  useEffect(() => {
    setAllSelectableProducts(
      (allProducts ?? [])
        .filter(product => product.vendor?.is_cisco === isCisco)
        .filter(product => !shouldOmitProductFromRelationshipUi(product, allProducts ?? []))
    );
  }, [allProducts, isCisco]);

  useEffect(() => {
    const getProducts = () => {
      if (productKind === 'customer' && customerId) return customerProducts ?? [];
      if (productKind === 'opportunity' && opportunityID) return opportunityProducts ?? [];
      return [];
    };

    const hydratedRows = hydrateProductList(getProducts(), allProducts ?? [])
      .filter((product: any) => product?.vendor?.is_cisco === isCisco)
      .filter((product: any) => !shouldOmitProductFromRelationshipUi(product, allProducts ?? []));

    const grouped = new Map<number, any[]>();

    hydratedRows.forEach((row) => {
      const productId = getBaseProductId(row);
      if (productId == null) return;
      if (!grouped.has(productId)) grouped.set(productId, []);
      grouped.get(productId).push(row);
    });

    const displayProducts = Array.from(grouped.entries()).map(([productId, rows]) => {
      const base = rows[0];
      const relationship = getRelationshipSelectionFromRows(rows, base, allProducts ?? []);

      return {
        ...base,
        relationship_rows: rows,
        deployments: relationship.deployments,
        deployment_name: rows
          .map((row) => getDeploymentNameFromRelationship(row, allProducts ?? []))
          .filter((value) => value !== EMPTY_DISPLAY)
          .filter((value, index, arr) => arr.indexOf(value) === index)
          .join(', ') || EMPTY_DISPLAY,
        version_name: rows
          .map((row) => getVersionNameFromRelationship(row))
          .filter((value) => value !== EMPTY_DISPLAY)
          .filter((value, index, arr) => arr.indexOf(value) === index)
          .join(', ') || EMPTY_DISPLAY,
        license_name: rows
          .map((row) => getLicenseNameFromRelationship(row))
          .filter((value) => value !== EMPTY_DISPLAY)
          .filter((value, index, arr) => arr.indexOf(value) === index)
          .join(', ') || EMPTY_DISPLAY,
        appliance_name: rows
          .map((row) => getApplianceNameFromRelationship(row))
          .filter((value) => value !== EMPTY_DISPLAY)
          .filter((value, index, arr) => arr.indexOf(value) === index)
          .join(', ') || EMPTY_DISPLAY,
      };
    });

    setSelectedProducts(displayProducts);
    setUpdatedSelectedProducts(displayProducts);

    const nextSelections = displayProducts.reduce((acc: Record<number, ProductRelationshipSelection>, product: any) => {
      const productId = getBaseProductId(product);
      if (productId != null) {
        acc[productId] = {
          deployments: product.deployments?.length
            ? product.deployments
            : getInitialDeploymentRowsForProduct(product, allProducts ?? []),
        };
      }
      return acc;
    }, {});

    setRelationshipSelections(nextSelections);
  }, [isCisco, productKind, opportunityID, customerId, customerProducts, opportunityProducts, allProducts]);

  const invalidateCaches = async () => {
    if (productKind === 'customer' && customerId) {
      await queryClient.invalidateQueries({ queryKey: [CacheKey.CustomerProduct, customerId] });
      await queryClient.invalidateQueries({ queryKey: [CacheKey.CustomerProductAssignment, customerId] });
    } else if (productKind === 'opportunity' && opportunityID) {
      await queryClient.invalidateQueries({ queryKey: [CacheKey.OpportunityProduct, opportunityID] });
      await queryClient.invalidateQueries({ queryKey: [CacheKey.OpportunityProductAssignment, opportunityID] });
    }
  };

  const getAssignmentProductId = (assignment: any) => {
    return (
      toNumberOrNull(assignment?.product_id) ||
      toNumberOrNull(assignment?.product) ||
      toNumberOrNull(assignment?.product?.id)
    );
  };

  const getAssignmentsForProductId = (productId: number | null) => {
    if (productId == null) return [];

    return (activeAssignments ?? []).filter((assignment: any) => {
      return Number(getAssignmentProductId(assignment)) === Number(productId);
    });
  };

  const setSelectionForProduct = (
    product: any,
    nextSelection: ProductRelationshipSelection
  ) => {
    const productId = getBaseProductId(product);
    if (productId == null) return;

    setRelationshipSelections((prev) => ({
      ...prev,
      [productId]: nextSelection,
    }));
  };

  const updateDeploymentRow = (
    product: any,
    rowId: string,
    patch: Partial<ProductDeploymentSelection>
  ) => {
    const productId = getBaseProductId(product);
    if (productId == null) return;

    setRelationshipSelections((prev) => {
      const current = prev[productId] ?? {
        deployments: getInitialDeploymentRowsForProduct(product, allProducts ?? []),
      };

      return {
        ...prev,
        [productId]: {
          deployments: current.deployments.map((row) => {
            if (row.row_id !== rowId) return row;

            const nextDeploymentId = Object.prototype.hasOwnProperty.call(patch, 'deployment_id')
              ? normalizeDeploymentId(patch.deployment_id)
              : row.deployment_id;

            const nextRow = {
              ...row,
              ...patch,
              deployment_id: nextDeploymentId,
            };

            if (isSaasDeployment(nextDeploymentId)) {
              nextRow.version_id = null;
            }

            if (Object.prototype.hasOwnProperty.call(patch, 'deployment_id')) {
              nextRow.version_id = null;
            }

            return nextRow;
          }),
        },
      };
    });
  };

  const addDeploymentRow = (product: any) => {
    const productId = getBaseProductId(product);
    if (productId == null) return;

    const offerType = getProductOfferType(product, allProducts ?? []);

    setRelationshipSelections((prev) => {
      const current = prev[productId] ?? {
        deployments: getInitialDeploymentRowsForProduct(product, allProducts ?? []),
      };

      const deployment_id =
        HYBRID_OFFER_TYPES.has(normalizeOfferType(offerType)) && current.deployments.length === 0
          ? SAAS_DEPLOYMENT_ID
          : null;

      return {
        ...prev,
        [productId]: {
          deployments: [
            ...current.deployments,
            {
              row_id: makeRelationshipRowId(),
              assignment_id: null,
              deployment_id,
              version_id: null,
              license_id: null,
            },
          ],
        },
      };
    });
  };

  const removeDeploymentRow = (product: any, rowId: string) => {
    const productId = getBaseProductId(product);
    if (productId == null) return;

    setRelationshipSelections((prev) => {
      const current = prev[productId] ?? {
        deployments: getInitialDeploymentRowsForProduct(product, allProducts ?? []),
      };

      const nextDeployments = current.deployments.filter((row) => row.row_id !== rowId);

      return {
        ...prev,
        [productId]: {
          deployments: nextDeployments.length
            ? nextDeployments
            : getInitialDeploymentRowsForProduct(product, allProducts ?? []),
        },
      };
    });
  };

  const handleRemoveModalCancel = async () => {
    setRemoveModalVisible(false);
    setStagedRemovedProducts([]);
  };

  const handleEditModalCancel = async () => {
    setEditModalVisible(false);
    setUpdatedSelectedProducts(selectedProducts);

    const nextSelections = selectedProducts.reduce((acc: Record<number, ProductRelationshipSelection>, product: any) => {
      const productId = getBaseProductId(product);
      if (productId != null) {
        acc[productId] = {
          deployments: product.deployments?.length
            ? product.deployments
            : getInitialDeploymentRowsForProduct(product, allProducts ?? []),
        };
      }
      return acc;
    }, {});

    setRelationshipSelections(nextSelections);
  };

  const handleRemoveProductClick = (product: any) => {
    setStagedRemovedProducts([product]);
    setRemoveModalVisible(true);
  };

  const handleProductTagClick = async (product: any) => {
    if (
      !product.vendor?.is_cisco ||
      stagedRemovedProducts.find((p) => getBaseProductId(p) === getBaseProductId(product))
    ) {
      return;
    }

    setSelectedProductDetails(product);
    setLoadingDcloudData(true);
    setDcloudData(null);
    setDcloudError(null);
    setDrawerVisible(true);

    try {
      const data = await fetchProductDemos(product.name, user?.email);
      setDcloudData(Array.isArray(data?.content) ? data.content : []);
    } catch (error: any) {
      console.warn('dCloud demos unavailable:', error);
      setDcloudData([]);
      setDcloudError(
        error?.message ||
        'dCloud demos are temporarily unavailable.'
      );
    } finally {
      setLoadingDcloudData(false);
    }
  };

  const handleProductSelectChange = (products: any[]) => {
    const hydratedProducts = hydrateProductList(products, allProducts ?? [])
      .filter((product) => !shouldOmitProductFromRelationshipUi(product, allProducts ?? []));

    setUpdatedSelectedProducts(hydratedProducts);

    setRelationshipSelections((prev) => {
      const next = { ...prev };

      hydratedProducts.forEach((product) => {
        const productId = getBaseProductId(product);
        if (productId == null) return;

        if (!next[productId]) {
          next[productId] = {
            deployments: getInitialDeploymentRowsForProduct(product, allProducts ?? []),
          };
        }
      });

      Object.keys(next).forEach((productId) => {
        if (!hydratedProducts.some((product) => Number(getBaseProductId(product)) === Number(productId))) {
          delete next[Number(productId)];
        }
      });

      return next;
    });
  };

  const deleteAssignmentIds = async (ids: any[]) => {
    const cleanIds = Array.from(new Set(ids.filter(Boolean)));

    if (!cleanIds.length) return;

    if (productKind === 'customer') {
      await deleteCustomerProducts(cleanIds);
    } else if (productKind === 'opportunity') {
      await deleteOpportunityProducts(cleanIds);
    }
  };

  const buildPayloadsForProduct = (product: any) => {
    const productId = getBaseProductId(product);
    if (productId == null) return [];

    const relationship = relationshipSelections[productId] ?? {
      deployments: getInitialDeploymentRowsForProduct(product, allProducts ?? []),
    };

    const rows = expandRelationshipSelectionsToRows({
      product,
      relationship,
      productCatalog: allProducts ?? [],
    });

    return rows.map((row) => {
      const rowRelationship = {
        deployment_id: row.deployment_id,
        version_id: row.version_id,
        license_id: row.license_id,
        appliance_id: row.appliance_id,
      };

      if (productKind === 'customer') {
        return buildCustomerOwnedProductPayload({
          product,
          relationship: rowRelationship,
          userEmail: user?.email,
          customerId,
        });
      }

      return buildOpportunityProductPayload({
        product,
        relationship: rowRelationship,
        userEmail: user?.email,
        opportunityID,
      });
    });
  };

  const handleSubmitChanges = async () => {
    setIsSubmitting(true);

    try {
      const idsToDelete: any[] = [];

      (activeAssignments ?? []).forEach((assignment: any) => {
        const productId = getAssignmentProductId(assignment);
        if (productId == null) return;

        // Adding or editing one product must never delete another product's assignments. Only
        // products still present in the working set are reconciled here; a product absent from it
        // (e.g. not yet loaded, or an id that didn't hydrate) is left untouched rather than deleted
        // by omission. Removal is a separate per-product action driven by the tag's remove control
        // (handleRemoveProductClick -> confirmRemoveProduct).
        const product = updatedSelectedProducts.find((item) => Number(getBaseProductId(item)) === Number(productId));
        if (!product) return;

        const currentSelection = getRelationshipSelectionFromRows(
          getAssignmentsForProductId(Number(productId)),
          product,
          allProducts ?? []
        );

        const nextSelection = relationshipSelections[Number(productId)] ?? {
          deployments: getInitialDeploymentRowsForProduct(product, allProducts ?? []),
        };

        if (deploymentSelectionKey(currentSelection) !== deploymentSelectionKey(nextSelection)) {
          idsToDelete.push(assignment.id);
        }
      });

      await deleteAssignmentIds(idsToDelete);

      const payloads: any[] = [];

      updatedSelectedProducts.forEach((product) => {
        const productId = getBaseProductId(product);
        if (productId == null) return;

        const existingAssignments = getAssignmentsForProductId(productId);
        const currentSelection = getRelationshipSelectionFromRows(
          existingAssignments,
          product,
          allProducts ?? []
        );

        const nextSelection = relationshipSelections[productId] ?? {
          deployments: getInitialDeploymentRowsForProduct(product, allProducts ?? []),
        };

        const isNew = existingAssignments.length === 0;
        const changed = deploymentSelectionKey(currentSelection) !== deploymentSelectionKey(nextSelection);

        if (isNew || changed) {
          payloads.push(...buildPayloadsForProduct(product));
        }
      });

      if (payloads.length > 0) {
        if (productKind === 'customer') {
          await addCustomerProducts(payloads as any);
        } else if (productKind === 'opportunity') {
          await addOpportunityProducts(payloads as any);
        }
      }
    } catch (error) {
      console.error('Error submitting changes:', error);
      throw error;
    } finally {
      await invalidateCaches();
      setIsSubmitting(false);
      setEditModalVisible(false);
      setStagedRemovedProducts([]);
    }
  };

  const confirmRemoveProduct = async () => {
    setIsSubmitting(true);

    try {
      const ids = stagedRemovedProducts.flatMap((product) => {
        const productId = getBaseProductId(product);
        return getAssignmentsForProductId(productId).map((assignment: any) => assignment.id);
      });

      await deleteAssignmentIds(ids);

      setStagedRemovedProducts([]);
      setRemoveModalVisible(false);
    } catch (error) {
      console.error('Error removing product:', error);
      throw error;
    } finally {
      await invalidateCaches();
      setIsSubmitting(false);
    }
  };

  /**
   * Scheduled demos cannot be launched from a link: they have to be booked. The
   * session manager owns that flow, so the menu items hand it the demo.
   */
  const openDcloudSessions = (demo?: any) => {
    if (demo) {
      const name = demo?.name || demo?.title || demo?.contentId || 'Demo';

      setDcloudScheduleTarget({
        demoId: getDcloudDemoId(demo),
        name,
        searchText: name,
      });
    } else {
      setDcloudScheduleTarget(null);
    }

    setDcloudSessionsVisible(true);
  };

  const renderDcloudDropdown = (dcloudData: any) => {
    const grouped = groupDemos(Array.isArray(dcloudData) ? dcloudData : []);
    const instant = grouped.instantDemos || [];
    const scheduled = grouped.scheduledDemos || [];

    if (!instant.length && !scheduled.length) {
      return <p>No demos available.</p>;
    }

    return (
      <HbrDropdown menu>
        <HbrButton slot="trigger" caret>
          dCloud Demos
        </HbrButton>

        <HbrMenu style={{ '--minwidth': '200px' } as React.CSSProperties}>
          <div style={{ fontWeight: 600, padding: '8px 16px' }}>
            Instant Demos
          </div>

          {instant.length ? (
            instant.map((demo: any) => {
              const href = demo?.links?.[0]?.href || demo?.href || demo?.url;
              const name = demo?.name || demo?.title || demo?.contentId || 'Demo';

              return (
                <HbrMenuItem
                  value={demo.contentId || name}
                  key={demo.contentId || name}
                  disabled={!href}
                  onClick={() => {
                    if (href) window.open(href, '_blank');
                  }}
                >
                  {name}
                </HbrMenuItem>
              );
            })
          ) : (
            <HbrMenuItem value="no-instant-demos" disabled>
              No instant demos
            </HbrMenuItem>
          )}

          <div style={{ fontWeight: 600, padding: '8px 16px' }}>
            Scheduled Demos
          </div>

          {scheduled.length ? (
            scheduled.map((demo: any) => {
              const name = demo?.name || demo?.title || demo?.contentId || 'Demo';

              return (
                <HbrMenuItem
                  value={demo.contentId || name}
                  key={demo.contentId || name}
                  onClick={() => openDcloudSessions(demo)}
                >
                  {name}
                </HbrMenuItem>
              );
            })
          ) : (
            <HbrMenuItem value="no-scheduled-demos" disabled>
              No scheduled demos
            </HbrMenuItem>
          )}

          <HbrMenuItem
            value="manage-dcloud-sessions"
            onClick={() => openDcloudSessions()}
          >
            Manage my dCloud sessions
          </HbrMenuItem>
        </HbrMenu>
      </HbrDropdown>
    );
  };

  const renderRelationshipSelectors = (product: any) => {
    const productId = getBaseProductId(product);
    if (productId == null) return null;

    const offerType = getProductOfferType(product, allProducts ?? []);
    const normalizedOfferType = normalizeOfferType(offerType);

    const showDeploymentRows = APPLIANCE_OFFER_TYPES.has(normalizedOfferType);
    const showLicense = shouldShowLicenseForProduct(product, allProducts ?? []);

    const selected = relationshipSelections[productId] ?? {
      deployments: getInitialDeploymentRowsForProduct(product, allProducts ?? []),
    };

    const deploymentRows = selected.deployments?.length
      ? selected.deployments
      : getInitialDeploymentRowsForProduct(product, allProducts ?? []);

    if (!showDeploymentRows && !showLicense) {
      return (
        <div className="text-xs italic text-gray-500">
          This product type does not support version, license, or deployment selections.
        </div>
      );
    }

    if (SAAS_OFFER_TYPES.has(normalizedOfferType)) {
      const row = deploymentRows[0] ?? getInitialDeploymentRowsForProduct(product, allProducts ?? [])[0];
      const licenses = getLicenseOptionsForDeployment(product, SAAS_DEPLOYMENT_ID);

      return (
        <div className="grid grid-cols-1 gap-2 mt-2">
          <HbrSelect
            label="License"
            value={row?.license_id != null ? String(row.license_id) : ''}
            placeholder="No license"
            clearable
            onHbr-change={(e: any) => {
              const value = asSingleValue(e.target.value);
              setSelectionForProduct(product, {
                deployments: [
                  {
                    ...(row ?? {
                      row_id: makeRelationshipRowId(),
                      assignment_id: null,
                      deployment_id: SAAS_DEPLOYMENT_ID,
                      version_id: null,
                      license_id: null,
                    }),
                    deployment_id: SAAS_DEPLOYMENT_ID,
                    version_id: null,
                    license_id: toNumberOrNull(value),
                  },
                ],
              });
            }}
          >
            <HbrOption value="">No license</HbrOption>
            {licenses.map((license) => (
              <HbrOption key={license.id} value={String(license.id)}>
                {displayValue(license.name)}
              </HbrOption>
            ))}
          </HbrSelect>

          <div className="text-xs text-gray-600">
            SaaS products support license selection only.
          </div>

          {!licenses.length && (
            <div className="text-xs italic text-gray-500">
              No license options are currently available for this SaaS product.
            </div>
          )}
        </div>
      );
    }

    const deploymentOptions = buildDeploymentOptions(product, allProducts ?? []);

    return (
      <div className="flex flex-col gap-3 mt-2">
        {deploymentRows.map((deploymentRow, index) => {
          const deploymentId = normalizeDeploymentId(deploymentRow.deployment_id);
          const isSaasRow = isSaasDeployment(deploymentId);
          const showVersion = shouldShowVersionForDeployment(product, deploymentId, allProducts ?? []);

          const versions = getVersionOptionsForDeployment(product, deploymentId);
          const licenses = getLicenseOptionsForDeployment(product, deploymentId);

          return (
            <div
              key={deploymentRow.row_id}
              className="border border-gray-200 rounded-md p-3 bg-white"
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="text-xs font-semibold text-gray-700">
                  Appliance/Deployment {index + 1}
                </div>

                {deploymentRows.length > 1 && (
                  <StbButton
                    iconOnly={false}
                    role="delete"
                    size="small"
                    variant="text"
                    onClick={() => removeDeploymentRow(product, deploymentRow.row_id)}
                  >
                    Remove
                  </StbButton>
                )}
              </div>

              <div className={`grid grid-cols-1 ${showVersion ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-2`}>
                <HbrSelect
                  label="Appliance/Deployment"
                  value={deploymentId != null ? String(deploymentId) : ''}
                  placeholder="Select deployment"
                  clearable
                  onHbr-change={(e: any) => {
                    const value = asSingleValue(e.target.value);
                    updateDeploymentRow(product, deploymentRow.row_id, {
                      deployment_id: normalizeDeploymentId(value),
                      version_id: null,
                    });
                  }}
                >
                  <HbrOption value="">Select deployment</HbrOption>
                  {deploymentOptions.map((option) => (
                    <HbrOption key={option.value} value={option.value}>
                      {option.label}
                    </HbrOption>
                  ))}
                </HbrSelect>

                {showVersion && (
                  <HbrSelect
                    label="Version"
                    value={deploymentRow.version_id != null ? String(deploymentRow.version_id) : ''}
                    placeholder="No version"
                    clearable
                    onHbr-change={(e: any) =>
                      updateDeploymentRow(product, deploymentRow.row_id, {
                        version_id: toNumberOrNull(asSingleValue(e.target.value)),
                      })
                    }
                  >
                    <HbrOption value="">No version</HbrOption>
                    {versions.map((version) => (
                      <HbrOption key={version.id} value={String(version.id)}>
                        {displayValue(version.version || version.name)}
                      </HbrOption>
                    ))}
                  </HbrSelect>
                )}

                <HbrSelect
                  label="License"
                  value={deploymentRow.license_id != null ? String(deploymentRow.license_id) : ''}
                  placeholder="No license"
                  clearable
                  onHbr-change={(e: any) =>
                    updateDeploymentRow(product, deploymentRow.row_id, {
                      license_id: toNumberOrNull(asSingleValue(e.target.value)),
                    })
                  }
                >
                  <HbrOption value="">No license</HbrOption>
                  {licenses.map((license) => (
                    <HbrOption key={license.id} value={String(license.id)}>
                      {displayValue(license.name)}
                    </HbrOption>
                  ))}
                </HbrSelect>
              </div>

              {HYBRID_OFFER_TYPES.has(normalizedOfferType) && isSaasRow && (
                <div className="text-xs text-gray-600 mt-2">
                  SaaS deployment selected. Only license selection applies.
                </div>
              )}

              {showVersion && !versions.length && (
                <div className="text-xs italic text-gray-500 mt-2">
                  No version options are currently available for this deployment.
                </div>
              )}

              {!licenses.length && (
                <div className="text-xs italic text-gray-500 mt-2">
                  No license options are currently available for this deployment.
                </div>
              )}
            </div>
          );
        })}

        <div>
          <HbrButton
            variant="outline"
            size="small"
            onClick={() => addDeploymentRow(product)}
          >
            + Add Appliance/Deployment
          </HbrButton>
        </div>

        <div className="text-xs text-gray-600">
          Appliance and hybrid products can include multiple deployment rows. Each row has its own deployment, version, and license selection.
        </div>
      </div>
    );
  };

  const renderRelationshipSummary = (product: any) => {
    const rows = product.relationship_rows?.length ? product.relationship_rows : [product];

    const entries = rows.map((row: any) => {
      const deploymentName = getDeploymentNameFromRelationship(row, allProducts ?? []);
      const versionName = getVersionNameFromRelationship(row);
      const licenseName = getLicenseNameFromRelationship(row);

      return [
        deploymentName !== EMPTY_DISPLAY ? deploymentName : '',
        versionName !== EMPTY_DISPLAY ? `Version: ${versionName}` : '',
        licenseName !== EMPTY_DISPLAY ? `License: ${licenseName}` : '',
      ].filter(Boolean).join(' • ');
    }).filter(Boolean);

    if (!entries.length) return null;

    return (
      <div className="text-[11px] font-normal leading-4 opacity-80 mt-0.5">
        {entries.map((entry, index) => (
          <div key={`${entry}-${index}`}>{entry}</div>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="flex flex-row items-center justify-between mb-2">
        <h3>{title}</h3>
        {editable && (
          <StbButton
            role="update"
            onClick={() => setEditModalVisible(true)}
            size="xsmall"
          />
        )}
      </div>

      <div className={`max-h-80 overflow-y-auto flex flex-wrap gap-1${selectedProducts.length ? ' mb-4' : ''}`}>
        {selectedProducts.map((product, i) => (
          <div
            className={`${
              product.vendor?.is_cisco
                ? 'bg-[#A7D194] hover:bg-success cursor-pointer'
                : 'bg-[#E4A09F]'
            } ${styles.tag}${editable ? ' hover:pr-1' : ''}`}
            key={`selected-${i}-${getBaseProductId(product)}-${product.vendor?.name}`}
            onClick={() => handleProductTagClick(product)}
            title={(product.relationship_rows?.length ? product.relationship_rows : [product])
              .map((row: any) => [
                product.name,
                getDeploymentNameFromRelationship(row, allProducts ?? []) !== EMPTY_DISPLAY
                  ? `Deployment: ${getDeploymentNameFromRelationship(row, allProducts ?? [])}`
                  : '',
                getVersionNameFromRelationship(row) !== EMPTY_DISPLAY
                  ? `Version: ${getVersionNameFromRelationship(row)}`
                  : '',
                getLicenseNameFromRelationship(row) !== EMPTY_DISPLAY
                  ? `License: ${getLicenseNameFromRelationship(row)}`
                  : '',
              ].filter(Boolean).join('\n'))
              .join('\n\n')}
          >
            <div className="flex flex-col">
              <span>{product.name}</span>
            </div>
            {editable && (
              <div className={styles.tagIcon}>
                <HbrIcon
                  asset={x}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveProductClick(product);
                  }}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      <StbModal
        title={isCisco ? "Cisco Products" : "Competitive Products"}
        open={editModalVisible}
        submitLabel={isSubmitting ? "Updating..." : "Update Products"}
        submitDisabled={isSubmitting || updatedSelectedProducts?.length === 0}
        role="update"
        onClose={handleEditModalCancel}
        onSubmit={handleSubmitChanges}
        size="large"
      >
        {allSelectableProducts.length === 0 && (
          <div><HbrSpinner size='small' />Loading products...</div>
        )}

        {allSelectableProducts.length !== 0 && (
          <div
            className="flex flex-col gap-4 overflow-y-auto pr-1"
            style={{
              maxHeight: 'min(72vh, 720px)',
            }}
          >
            <StbMultiSelectGroupedWithSearch
              items={allSelectableProducts.map(product => ({ ...product, vendorName: product.vendor.name }))}
              selected={updatedSelectedProducts.map(product => ({ ...product, vendorName: product.vendor.name }))}
              groupBy={'vendorName'}
              onChange={(selected) => handleProductSelectChange([...selected].map(({ vendorName, ...rest }) => rest))}
              className="w-full"
              inline={true}
            />

            {!!updatedSelectedProducts.length && (
              <Accordion
                defaultExpanded={false}
                className="border-t border-gray-300 pt-2"
                disableGutters
                elevation={0}
                sx={{
                  '&:before': {
                    display: 'none',
                  },
                  backgroundColor: 'transparent',
                }}
              >
                <AccordionSummary
                  expandIcon={<span className="text-lg leading-none">▾</span>}
                  sx={{
                    padding: 0,
                    minHeight: '36px',
                    '& .MuiAccordionSummary-content': {
                      margin: 0,
                      alignItems: 'center',
                    },
                  }}
                >
                  <div className="flex flex-col">
                    <h4 className="mt-0 mb-0">Deployment, Version, and License</h4>
                    <span className="text-xs text-gray-500 font-normal">
          Expand to configure appliance, version, and license selections.
        </span>
                  </div>
                </AccordionSummary>

                <AccordionDetails
                  sx={{
                    padding: 0,
                    paddingTop: '0.75rem',
                  }}
                >
                  <div
                    className="flex flex-col gap-3 overflow-y-auto pr-1"
                    style={{
                      maxHeight: 'min(52vh, 420px)',
                    }}
                  >
                    {updatedSelectedProducts
                      .slice()
                      .sort((a, b) => (a?.name ?? '').localeCompare(b?.name ?? ''))
                      .map((product) => {
                        const productId = getBaseProductId(product);
                        const offerType = getProductOfferType(product, allProducts ?? []) || 'unknown offer type';
                        const selected = productId != null ? relationshipSelections[productId] : null;
                        const deploymentCount = selected?.deployments?.length ?? 0;

                        return (
                          <Accordion
                            key={`relationship-${productId}`}
                            defaultExpanded={false}
                            disableGutters
                            elevation={0}
                            className="border border-gray-300 rounded-md bg-white"
                            sx={{
                              '&:before': {
                                display: 'none',
                              },
                              backgroundColor: 'white',
                              borderRadius: '0.375rem',
                            }}
                          >
                            <AccordionSummary
                              expandIcon={<span className="text-lg leading-none">▾</span>}
                              sx={{
                                minHeight: '44px',
                                padding: '0.75rem',
                                '& .MuiAccordionSummary-content': {
                                  margin: 0,
                                },
                              }}
                            >
                              <div className="flex flex-col w-full">
                                <div className="font-semibold">{product.name}</div>
                                <div className="text-xs text-gray-600">
                                  {offerType}
                                  {deploymentCount > 0 ? ` • ${deploymentCount} selection${deploymentCount === 1 ? '' : 's'}` : ''}
                                </div>
                              </div>
                            </AccordionSummary>

                            <AccordionDetails
                              sx={{
                                padding: '0 0.75rem 0.75rem 0.75rem',
                              }}
                            >
                              {renderRelationshipSelectors(product)}
                            </AccordionDetails>
                          </Accordion>
                        );
                      })}
                  </div>
                </AccordionDetails>
              </Accordion>
            )}
          </div>
        )}
      </StbModal>

      <StbModal
        role="delete"
        open={removeModalVisible}
        onSubmit={confirmRemoveProduct}
        onClose={handleRemoveModalCancel}
      >
        <div className='modal-body'>
          <p>Are you sure you want to remove {stagedRemovedProducts[0] ? <em>{stagedRemovedProducts[0]?.name}</em> : 'this product'}?</p>
        </div>
      </StbModal>

      <HbrDrawer
        open={drawerVisible}
        scrollAware={true}
        id='product-detail-drawer'
        onHbr-hide={() => setDrawerVisible(false)}
      >
        <div slot='label' className='hbr-css__layout-col-2xs'>
          <h2>
            {selectedProductDetails?.name}
          </h2>
          <p className='hbr-css__text-secondary-sm'>
            {selectedProductDetails?.description}
          </p>
          {selectedProductDetails && (
            <p className='hbr-css__text-secondary-sm'>
              {(selectedProductDetails.relationship_rows?.length ? selectedProductDetails.relationship_rows : [selectedProductDetails])
                .map((row: any) => [
                  getDeploymentNameFromRelationship(row, allProducts ?? []) !== EMPTY_DISPLAY
                    ? `Deployment: ${getDeploymentNameFromRelationship(row, allProducts ?? [])}`
                    : '',
                  getVersionNameFromRelationship(row) !== EMPTY_DISPLAY
                    ? `Version: ${getVersionNameFromRelationship(row)}`
                    : '',
                  getLicenseNameFromRelationship(row) !== EMPTY_DISPLAY
                    ? `License: ${getLicenseNameFromRelationship(row)}`
                    : '',
                ].filter(Boolean).join(' • '))
                .filter(Boolean)
                .join('\n')}
            </p>
          )}
        </div>
        <div style={{ width: '100%', minHeight: '150%' }}>
          {selectedProductDetails ? (
            <div>
              <h3>Resources</h3>
              <ul style={{ listStyleType: 'none', paddingLeft: 0 }}>
                <li>
                  <a
                    href={selectedProductDetails.sales_url}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    Public Product Site
                  </a>
                </li>
                <li>
                  <a
                    href={selectedProductDetails.sales_connect_url}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    SalesConnect Content
                  </a>
                </li>
              </ul>
              <div className="flex flex-wrap items-center gap-3">
                {loadingDcloudData ? (
                  <p>Loading demos...</p>
                ) : dcloudError ? (
                  <p className="text-sm text-gray-500">
                    dCloud demos are temporarily unavailable.
                  </p>
                ) : dcloudData && dcloudData.length > 0 ? (
                  renderDcloudDropdown(dcloudData)
                ) : (
                  <p>No demos available.</p>
                )}

                <StbButton
                  role="default"
                  size="small"
                  onClick={async () => openDcloudSessions()}
                >
                  My dCloud sessions
                </StbButton>
              </div>

              <h3>
                {productKind === 'opportunity' ? 'Planned Deployment' : 'Current Deployment'}
              </h3>
              <div className="text-sm">
                {(selectedProductDetails.relationship_rows?.length ? selectedProductDetails.relationship_rows : [selectedProductDetails])
                  .map((row: any, index: number) => (
                    <dl key={`selected-relationship-${index}`} className="grid grid-cols-[8rem_1fr] gap-x-2 gap-y-1 mb-3">
                      <dt className="font-semibold">Deployment</dt>
                      <dd>{getDeploymentNameFromRelationship(row, allProducts ?? [])}</dd>
                      <dt className="font-semibold">Version</dt>
                      <dd>{getVersionNameFromRelationship(row)}</dd>
                      <dt className="font-semibold">License</dt>
                      <dd>{getLicenseNameFromRelationship(row)}</dd>
                    </dl>
                  ))}
              </div>

              <h3>Versions</h3>
              {selectedProductDetails.version && selectedProductDetails.version.length ? (
                selectedProductDetails.version.map((version) => (
                  <Accordion key={version.id}>
                    <AccordionSummary>
                      <p className="hbr-css__text-secondary-sm">{`${version.version}`}</p>
                    </AccordionSummary>
                    <AccordionDetails>
                      <strong>Released:</strong>{' '}
                      {version.released
                        ? new Date(version.released).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                        })
                        : '—'} <br />
                      <a
                        href={version.release_notes_url}
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        Release Notes
                      </a>
                      <br />
                      <a
                        href={version.configuration_guide}
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        Configuration Guide
                      </a>
                      <br />
                      <a
                        href={version.api_documentation}
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        API Documentation
                      </a>
                    </AccordionDetails>
                  </Accordion>
                ))
              ) : (
                <p>No versions available.</p>
              )}
            </div>
          ) : (
            <p>Loading...</p>
          )}
        </div>
        <div slot='footer'>
          <div className='hbr-css__button-group hbr-css__button-group-raised hbr-css__button-group-end'>
            <StbButton role="default" onClick={() => setDrawerVisible(false)} />
          </div>
        </div>
      </HbrDrawer>

      <HbrDrawer
        open={dcloudSessionsVisible}
        scrollAware={true}
        id='dcloud-sessions-drawer'
        onHbr-hide={() => setDcloudSessionsVisible(false)}
      >
        <div slot='label' className='hbr-css__layout-col-2xs'>
          <h2>dCloud sessions</h2>
          <p className='hbr-css__text-secondary-sm'>
            Schedule, edit, and end your dCloud sessions.
          </p>
        </div>

        <div style={{ width: '100%' }}>
          {dcloudSessionsVisible && (
            <DcloudSessionManager
              variant='drawer'
              initialTarget={dcloudScheduleTarget}
              autoOpenSchedule={Boolean(dcloudScheduleTarget)}
            />
          )}
        </div>

        <div slot='footer'>
          <div className='hbr-css__button-group hbr-css__button-group-raised hbr-css__button-group-end'>
            <StbButton role="default" onClick={() => setDcloudSessionsVisible(false)} />
          </div>
        </div>
      </HbrDrawer>
    </>
  );
};

export default ProductTagManager;